/**
 * `data-screen-label="Agent"`, tab `questions` — the survey question editor (plan-030 unit **WAPP-Q**; design §41.10;
 * glossary §6.45; ADR 0084, proposed). A survey VoiceAgent's questionnaire as groups of typed question cards, edited
 * here and saved as a DRAFT the owner publishes, without a new upload or a build.
 *
 * ```
 *   read     GET  …/questionnaire-document            the version the editor edits (or another, read-only)
 *   check    POST …/questionnaire-document/check      on each pause while there are unsaved changes; never writes
 *   save     PUT  …/questionnaire-document            If-Match = the version edited → a draft built on the live version
 *   publish  the Versions tab's own publish            ADR 0043, with acknowledge_base_change when the API asks
 *   download GET  …/questionnaire-document/file       JSON (uploaded back through Edit it is used as it is) or Markdown
 * ```
 *
 * THE STATES OF §41.10, EACH DRAWN: reading and a failed read (Try again); no questions to show (building, not a
 * questionnaire — the reason and Versions); read-only while editing is switched off ("Editing is not available yet",
 * coming soon, the download still works), for another version ("Edit the version the editor edits"), and for an
 * archived agent; editing — the groups, the cards with type, code and wording that open to their fields, Add question
 * (a type picker) at the end and below any card, Move up / Move down / Remove on each card, the five replies and the
 * spoken lines; unsaved changes — a sticky bar with the check's problem and warning counts (a click jumps to the first
 * problem), Discard and Save as a draft (disabled while there are problems); problems on their card and field;
 * warnings as amber notes and "check the wording" chips on the words wetalk added; saved — "Saved as draft vN, built on
 * vM", Publish now and View in Versions, with the campaigns calling with this agent named before the publish (OD-8);
 * `changed: false` — "Nothing changed: nothing was saved"; stale — someone saved or published meanwhile (Reload, and the
 * local edits kept to copy). A questionnaire in the first format is shown in the current one, and the owner is told the
 * feelings and news questions are now numbered like the others (OD-6, AC-7).
 *
 * Release 2's Rules panel and Release 3's "Describe a rule" are not drawn here: `rule_available` and
 * `instruction_reading_available` are false until plan-030 WAPP-R and WAPP-I.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Icon, Input, Tag, Textarea } from "@wetalk/ui";
import { questionnaireFileNameOf, type SurveyVariant } from "@wetalk/domain";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { apiProblemText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import { QUERY_KEY, agentVersionListQuery, voiceAgentQuery } from "../../../../api/query.js";
import { draftBaseChangedOf } from "../../../../api/slice/optimisation-review.js";
import {
  SURVEY_QUESTIONNAIRE_QUERY_KEY,
  questionnaireRefusalOf,
  saveQuestionnaireFile,
  useQuestionnaireDocument,
  type CampaignDiallingView,
  type QuestionnaireCheckView,
  type QuestionnaireDocumentView,
  type QuestionnaireFileFormatView,
  type QuestionnaireNoteView,
  type QuestionnaireQuestionType,
  type QuestionnaireSurveyJson,
} from "../../../../api/slice/survey-questionnaire.js";
import { usePortalTranslation } from "../../../../i18n/portal.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { QuestionCard, type LocatedNote, type QuestionCardEdit, type QuestionCardProps } from "./question-card.js";
import { editorText, groupLabel, languageLabel, problemText, questionTypeLabel, warningText } from "./questionnaire-copy.js";
import {
  OWNER_REPLY,
  SPOKEN_LINE,
  freeCodeOf,
  groupsOf,
  languagesOf,
  lineOf,
  locationOf,
  ordinalsOf,
  questionsOf,
  sameSurvey,
  withLine,
  withNumberRange,
  withOptionAdded,
  withOptionKeywords,
  withOptionLabel,
  withOptionRemoved,
  withOrdinals,
  withQuestionAdded,
  withQuestionCode,
  withQuestionMoved,
  withQuestionRemoved,
  withQuestionWording,
  withSelection,
  withSelectionMore,
} from "./questionnaire-model.js";

import "./questions.css";

/** How long the editor waits after the last change before it asks the check route. */
export const CHECK_PAUSE_MS = 800;

const t = editorText;

/** The two sections' headings, keyed beside the cards' (a question code never starts with "#"). */
const SECTION_REPLIES = "#replies";
const SECTION_LINES = "#lines";

export interface QuestionsTabProps {
  readonly voiceAgentId: string;
  /** Another version to show read-only; `null` for the version the editor edits. */
  readonly agentVersionId: string | null;
  readonly onShowAgentVersion: (agentVersionId: string | null) => void;
  /** Versions, on a version (`null`: as it opens). */
  readonly onOpenAgentVersion: (agentVersionId: string | null) => void;
  /** The pause before a check; a suite passes 0. */
  readonly checkPauseMs?: number;
}

/** What the last save, publish or refusal did — kept above the editor, which remounts on the new draft. */
type Outcome =
  | { readonly kind: "saved"; readonly draftAgentVersionId: string; readonly draftLabel: string; readonly baseAgentVersionId: string | null }
  | { readonly kind: "unchanged"; readonly label: string }
  | { readonly kind: "stale"; readonly label: string | null }
  | { readonly kind: "published"; readonly label: string }
  | { readonly kind: "base_changed"; readonly label: string; readonly agentVersionId: string; readonly message: string }
  | { readonly kind: "publish_failed"; readonly message: string };

export function QuestionsTab(props: QuestionsTabProps): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  const document = useQuestionnaireDocument(props.voiceAgentId, props.agentVersionId);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (document.data === undefined) {
    if (document.isError) {
      return (
        <section className="c-qn" role="alert">
          <p className="c-note c-note-critical">
            {t("state.read_failed")} {apiProblemText(document.error, "")}
          </p>
          <Button
            variant="primary"
            disabled={document.isFetching}
            onClick={() => {
              void document.refetch();
            }}
          >
            {t("action.try_again")}
          </Button>
        </section>
      );
    }
    return (
      <section className="c-qn" aria-busy="true">
        <p className="c-note">{t("state.reading")}</p>
        <div className="c-qn-skeleton" aria-hidden="true" />
      </section>
    );
  }

  const one = document.data;
  if (one.survey === null || one.agentVersionId === null) {
    return (
      <section className="c-qn">
        <p className="c-note" role="status">
          {one.notEditableReason === "building" ? t("state.building") : t("state.not_a_questionnaire")}
        </p>
        <Button
          variant="secondary"
          onClick={() => {
            props.onOpenAgentVersion(null);
          }}
        >
          {t("action.open_versions")}
        </Button>
      </section>
    );
  }

  return (
    <QuestionnaireEditor
      key={`${one.agentVersionId}:${String(one.editable)}`}
      voiceAgentId={props.voiceAgentId}
      document={one}
      survey={one.survey}
      agentVersionId={one.agentVersionId}
      outcome={outcome}
      onOutcome={setOutcome}
      onShowAgentVersion={props.onShowAgentVersion}
      onOpenAgentVersion={props.onOpenAgentVersion}
      checkPauseMs={props.checkPauseMs ?? CHECK_PAUSE_MS}
    />
  );
}

interface EditorProps {
  readonly voiceAgentId: string;
  readonly document: QuestionnaireDocumentView;
  readonly survey: QuestionnaireSurveyJson;
  readonly agentVersionId: string;
  readonly outcome: Outcome | null;
  readonly onOutcome: (outcome: Outcome | null) => void;
  readonly onShowAgentVersion: (agentVersionId: string | null) => void;
  readonly onOpenAgentVersion: (agentVersionId: string | null) => void;
  readonly checkPauseMs: number;
}

/** The last check, with the map it was made on (so its paths resolve against what was sent). */
interface CheckedState {
  readonly sent: QuestionnaireSurveyJson;
  readonly check: QuestionnaireCheckView;
}

function wordingKeyOf(language: string | null, questionId: string | null, field: string): string {
  return `${language ?? ""}|${questionId ?? ""}|${field}`;
}

function QuestionnaireEditor(props: EditorProps): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const { document: doc } = props;
  const voiceAgent = useQuery(voiceAgentQuery(gateway, props.voiceAgentId));
  const agentVersion = useQuery(agentVersionListQuery(gateway, props.voiceAgentId));
  const writeRefusal = useMemberCan().whyNot("voice_agent.write");
  const readOnly = !doc.editable || writeRefusal !== null;

  const [survey, setSurvey] = useState<QuestionnaireSurveyJson>(props.survey);
  const languages = languagesOf(props.survey);
  const groups = groupsOf(props.survey);
  const [language, setLanguage] = useState<string>(languages[0] ?? "");
  const [variant, setVariant] = useState<SurveyVariant>(groups[0] ?? "A");
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [adding, setAdding] = useState<number | null>(null);
  const [changeNote, setChangeNote] = useState("");
  const [checked, setChecked] = useState<CheckedState | null>(null);
  /** Words wetalk added that the owner has not edited yet (OD-7), by language, question code and field. */
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
  /** Where the focus goes after the render an edit causes: set in a handler, taken by the effect below. */
  const focusAfter = useRef<{ readonly questionId: string; readonly target: "heading" | "up" | "down" } | null>(null);
  const setFocusAfter = (next: { readonly questionId: string; readonly target: "heading" | "up" | "down" }): void => {
    focusAfter.current = next;
  };
  const headingRef = useRef(new Map<string, HTMLHeadingElement>());
  const [, setFocusTick] = useState(0);
  const sectionRef = (key: string) => (element: HTMLHeadingElement | null): void => {
    if (element === null) headingRef.current.delete(key);
    else headingRef.current.set(key, element);
  };
  const upRef = useRef(new Map<string, HTMLButtonElement>());
  const downRef = useRef(new Map<string, HTMLButtonElement>());

  const firstFormat = doc.format === "surveybot-survey-json-v1";
  const dirty = !sameSurvey(survey, props.survey);
  const savable = dirty || firstFormat;
  const historyCode = useMemo(
    () => [...doc.questionCodeHistory.map((one) => one.questionCode), ...groupsOf(props.survey).flatMap((group) => questionsOf(props.survey, languagesOf(props.survey)[0] ?? "", group).map((one) => one.id))],
    [doc.questionCodeHistory, props.survey],
  );
  const baseCode = useMemo(() => new Set(historyCode), [historyCode]);

  // ── the check, on each pause ──────────────────────────────────────────────────────────────────────────
  // The map on screen, for a check's answer to know whether it is still the map it was made on.
  const latest = useRef(survey);
  useEffect(() => {
    latest.current = survey;
  }, [survey]);
  const check = useMutation({
    mutationFn: (sent: QuestionnaireSurveyJson) =>
      gateway.checkQuestionnaireDocument(props.voiceAgentId, props.agentVersionId, { survey: sent, changeNote: null }).then((answer) => ({ sent, answer })),
    onSuccess({ sent, answer }) {
      if (latest.current !== sent) return;
      setChecked({ sent, check: answer });
      // Adopt the words wetalk added, and mark each until the owner edits it.
      const marked = answer.warning.filter((one) => one.code === "wording_added");
      if (marked.length > 0) {
        setAdded((current) => {
          const next = new Set(current);
          for (const one of marked) {
            const at = locationOf(one.path, answer.survey ?? sent);
            next.add(wordingKeyOf(at.language, at.questionId, at.field));
          }
          return next;
        });
      }
      if (answer.survey !== null && !sameSurvey(answer.survey, sent)) setSurvey(answer.survey);
    },
  });
  const checkMutate = check.mutate;
  useEffect(() => {
    if (readOnly || !savable) return undefined;
    const timer = setTimeout(() => {
      checkMutate(survey);
    }, props.checkPauseMs);
    return () => {
      clearTimeout(timer);
    };
  }, [survey, readOnly, savable, props.checkPauseMs, checkMutate]);

  const current = checked !== null && checked.sent === survey ? checked : null;
  const located = (notes: readonly QuestionnaireNoteView[], sent: QuestionnaireSurveyJson): readonly LocatedNote[] =>
    notes.map((note) => ({ note, location: locationOf(note.path, sent) }));

  // ── the save ──────────────────────────────────────────────────────────────────────────────────────────
  const [refusedProblem, setRefusedProblem] = useState<{ readonly sent: QuestionnaireSurveyJson; readonly problem: readonly QuestionnaireNoteView[] } | null>(null);
  const save = useMutation({
    mutationFn: (sent: QuestionnaireSurveyJson) =>
      gateway.saveQuestionnaireDocument(props.voiceAgentId, props.agentVersionId, {
        survey: sent,
        changeNote: changeNote.trim() === "" ? null : changeNote.trim(),
      }),
    async onSuccess(saved) {
      if (!saved.changed || saved.draftAgentVersionId === null) {
        props.onOutcome({ kind: "unchanged", label: doc.agentVersionLabel ?? "" });
        return;
      }
      props.onOutcome({
        kind: "saved",
        draftAgentVersionId: saved.draftAgentVersionId,
        draftLabel: saved.draftLabel ?? "",
        baseAgentVersionId: saved.baseAgentVersionId,
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      await queryClient.invalidateQueries({ queryKey: SURVEY_QUESTIONNAIRE_QUERY_KEY.all });
    },
    onError(error, sent) {
      const refused = questionnaireRefusalOf(error);
      if (refused !== null && refused.code === "questionnaire_base_stale") {
        props.onOutcome({ kind: "stale", label: refused.editable?.agentVersionLabel ?? null });
        return;
      }
      if (refused !== null && refused.code === "questionnaire_invalid") setRefusedProblem({ sent, problem: refused.problem });
    },
  });

  const problemNotes: readonly LocatedNote[] =
    refusedProblem !== null && refusedProblem.sent === survey
      ? located(refusedProblem.problem, refusedProblem.sent)
      : current === null
        ? []
        : located(current.check.problem, current.sent);
  const warningNotes: readonly LocatedNote[] = current === null ? [] : located(current.check.warning.filter((one) => one.code !== "wording_added"), current.sent);
  const readWarning = located(doc.warning.filter((one) => one.code !== "format_converted" && one.code !== "wording_added"), props.survey);

  const change = (next: QuestionnaireSurveyJson): void => {
    setSurvey(next);
  };
  const touch = (key: string): void => {
    if (!added.has(key)) return;
    setAdded((currentSet) => {
      const next = new Set(currentSet);
      next.delete(key);
      return next;
    });
  };

  // Focus follows a moved card, and a problem jump lands on its card's heading.
  useEffect(() => {
    const wanted = focusAfter.current;
    if (wanted === null) return;
    const map = wanted.target === "heading" ? headingRef.current : wanted.target === "up" ? upRef.current : downRef.current;
    const element = map.get(wanted.questionId);
    if (element === undefined) return;
    focusAfter.current = null;
    if (wanted.target === "heading" && typeof element.scrollIntoView === "function") element.scrollIntoView({ block: "center" });
    // A disabled button cannot take the focus: the heading does instead.
    if (element instanceof HTMLButtonElement && element.disabled) headingRef.current.get(wanted.questionId)?.focus();
    else element.focus();
  });

  const questions = questionsOf(survey, language, variant);
  const addQuestion = (index: number, type: QuestionnaireQuestionType): void => {
    const code = freeCodeOf(survey, historyCode);
    change(withQuestionAdded(survey, variant, index, type, code));
    setOpen((was) => new Set(was).add(code));
    setAdding(null);
    setFocusAfter({ questionId: code, target: "heading" });
  };

  /** The bar's count jumps to the first problem: a question's card, opened, or else the section of the line. */
  const jumpToFirstProblem = (): void => {
    const first = problemNotes.find((one) => one.location.questionId !== null) ?? problemNotes[0];
    if (first === undefined) return;
    if (first.location.language !== null && languages.includes(first.location.language)) setLanguage(first.location.language);
    if (first.location.variant !== null) setVariant(first.location.variant);
    const questionId = first.location.questionId;
    if (questionId !== null) {
      setOpen((was) => new Set(was).add(questionId));
      setFocusAfter({ questionId, target: "heading" });
    } else {
      setFocusAfter({ questionId: first.location.field.startsWith("objections.") ? SECTION_REPLIES : SECTION_LINES, target: "heading" });
    }
    // The language or the group may not change, so nothing else re-renders: focus now as well.
    setFocusTick((tick) => tick + 1);
  };

  const editOf = (index: number, questionId: string): QuestionCardEdit => ({
    wording: (key, value) => {
      touch(wordingKeyOf(language, questionId, key));
      change(withQuestionWording(survey, language, variant, index, key, value));
    },
    code: (code) => {
      change(withQuestionCode(survey, variant, index, code));
      setOpen((was) => new Set([...was].filter((one) => one !== questionId)).add(code));
    },
    selection: (next) => {
      change(withSelection(survey, variant, index, next));
    },
    more: (value) => {
      touch(wordingKeyOf(language, questionId, "selection.more"));
      change(withSelectionMore(survey, language, variant, index, value));
    },
    range: (next) => {
      change(withNumberRange(survey, variant, index, next));
    },
    optionAdded: () => {
      change(withOptionAdded(survey, variant, index));
    },
    optionRemoved: (optionIndex) => {
      change(withOptionRemoved(survey, variant, index, optionIndex));
    },
    optionLabel: (optionIndex, label) => {
      change(withOptionLabel(survey, language, variant, index, optionIndex, label));
    },
    optionKeywords: (optionIndex, line) => {
      change(withOptionKeywords(survey, language, variant, index, optionIndex, line));
    },
    moved: (step) => {
      change(withQuestionMoved(survey, variant, index, step));
      setFocusAfter({ questionId, target: step === -1 ? "up" : "down" });
    },
    removed: () => {
      change(withQuestionRemoved(survey, variant, index));
    },
    addBelow: () => {
      setAdding(index + 1);
    },
  });

  const shownLabel = doc.agentVersionLabel ?? "";
  const ordinal = ordinalsOf(survey[language], variant);
  const file = survey[language];
  const fileProblem = problemNotes.filter((one) => one.location.questionId === null);
  const lineError = (path: string): { readonly error?: string } => {
    const found = fileProblem.filter((one) => one.location.language === language && one.location.field === path);
    return found.length === 0 ? {} : { error: found.map((one) => problemText(one.note)).join(" ") };
  };
  // A problem of a line the page draws for the shown language sits on its field; every other one is listed here.
  const drawnLine = new Set<string>([...SPOKEN_LINE, ...OWNER_REPLY.map((reply) => `objections.${reply}.answer`), ordinal.path]);
  const otherProblem = fileProblem.filter((one) => !(one.location.language === language && drawnLine.has(one.location.field)));
  const platformLine = doc.platformLine[language] ?? null;
  const problemCount = problemNotes.length;
  const warningCount = (current?.check.warning.length ?? 0);

  return (
    <section className="c-qn">
      <div className="c-va-titlerow c-va-titlerow-loose">
        <div>
          <p className="c-va-summary">
            {doc.isDraft ? t("head.draft", { label: shownLabel }) : t("head.version", { label: shownLabel })}
          </p>
          <p className="c-va-subtitle">{t("head.title")}</p>
        </div>
        <DownloadActions
          voiceAgentId={props.voiceAgentId}
          agentVersionId={props.agentVersionId}
          agentVersionLabel={shownLabel}
          languages={languages}
          language={language}
          displayName={voiceAgent.data?.displayName ?? ""}
        />
      </div>

      {firstFormat ? (
        <p className="c-va-alert" role="note">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.info} size="lg" />
          </span>
          <span>{t("notice.format_converted")}</span>
        </p>
      ) : null}

      <NotEditableNotice document={doc} writeRefusal={writeRefusal} onShowAgentVersion={props.onShowAgentVersion} />

      <OutcomeNotice
        voiceAgentId={props.voiceAgentId}
        document={doc}
        outcome={props.outcome}
        onOutcome={props.onOutcome}
        baseLabelOf={(agentVersionId) =>
          agentVersion.data?.find((one) => one.agentVersionId === agentVersionId)?.label ?? voiceAgent.data?.liveAgentVersionLabel ?? null
        }
        liveVersionSeq={voiceAgent.data?.liveVersionSeq ?? null}
        survey={survey}
        onOpenAgentVersion={props.onOpenAgentVersion}
      />

      {languages.length < 2 ? null : (
        <div className="c-va-seg c-qn-seg" role="group" aria-label={t("language.label")}>
          {languages.map((code) => (
            <button
              type="button"
              key={code}
              className="c-va-seg-opt"
              aria-pressed={code === language}
              onClick={() => {
                setLanguage(code);
              }}
            >
              {languageLabel(code)}
            </button>
          ))}
        </div>
      )}
      {groups.length < 2 ? null : (
        <div className="c-va-subtabs c-qn-groups" role="tablist" aria-label={t("group.label")}>
          {groups.map((one) => (
            <button
              type="button"
              role="tab"
              key={one}
              className="c-va-subtab"
              aria-selected={one === variant}
              onClick={() => {
                setVariant(one);
              }}
            >
              {groupLabel(one)}
            </button>
          ))}
        </div>
      )}

      <ol className="c-qn-cards" aria-label={t("cards.label", { group: groupLabel(variant) })}>
        {questions.map((question, index) => (
          <QuestionCardSlot
            key={`${question.id}:${String(index)}`}
            index={index}
            count={questions.length}
            question={question}
            language={language}
            readOnly={readOnly}
            open={open.has(question.id)}
            onOpen={(isOpen) => {
              setOpen((was) => {
                const next = new Set(was);
                if (isOpen) next.add(question.id);
                else next.delete(question.id);
                return next;
              });
            }}
            isNew={!baseCode.has(question.id)}
            problem={problemNotes.filter((one) => one.location.questionId === question.id && one.location.variant === variant)}
            warning={[...readWarning, ...warningNotes].filter((one) => one.location.questionId === question.id && one.location.variant === variant)}
            wordingAdded={(field) => added.has(wordingKeyOf(language, question.id, field))}
            edit={editOf(index, question.id)}
            headingRef={(element) => {
              if (element === null) headingRef.current.delete(question.id);
              else headingRef.current.set(question.id, element);
            }}
            moveUpRef={(element) => {
              if (element === null) upRef.current.delete(question.id);
              else upRef.current.set(question.id, element);
            }}
            moveDownRef={(element) => {
              if (element === null) downRef.current.delete(question.id);
              else downRef.current.set(question.id, element);
            }}
            adding={adding === index + 1}
            onAdd={(type) => {
              addQuestion(index + 1, type);
            }}
            onCancelAdd={() => {
              setAdding(null);
            }}
          />
        ))}
      </ol>

      {readOnly ? null : (
        <AddQuestion
          open={adding === questions.length}
          onOpen={() => {
            setAdding(questions.length);
          }}
          onAdd={(type) => {
            addQuestion(questions.length, type);
          }}
          onCancel={() => {
            setAdding(null);
          }}
        />
      )}

      <section className="c-qn-section c-va-card" aria-labelledby="c-qn-replies-title">
        <h3 className="c-va-card-title" id="c-qn-replies-title" tabIndex={-1} ref={sectionRef(SECTION_REPLIES)}>
          {t("replies.title")}
        </h3>
        <p className="c-va-card-dek">{t("replies.dek")}</p>
        {OWNER_REPLY.map((reply) => {
          const path = `objections.${reply}.answer`;
          const value = lineOf(file, path);
          return readOnly ? (
            <div key={reply} className="c-qn-readonly-line">
              <p className="c-qn-line-label">{t(`replies.${reply}`)}</p>
              <p className="c-qn-line-text">{value}</p>
            </div>
          ) : (
            <Field key={reply} label={t(`replies.${reply}`)} {...lineError(path)}>
              {(control) => (
                <Textarea
                  {...control}
                  value={value}
                  onChange={(event) => {
                    change(withLine(survey, language, path, event.currentTarget.value));
                  }}
                />
              )}
            </Field>
          );
        })}
      </section>

      <section className="c-qn-section c-va-card" aria-labelledby="c-qn-lines-title">
        <h3 className="c-va-card-title" id="c-qn-lines-title" tabIndex={-1} ref={sectionRef(SECTION_LINES)}>
          {t("lines.title")}
        </h3>
        <p className="c-va-card-dek">{t("lines.dek")}</p>
        {SPOKEN_LINE.map((path) => {
          const value = lineOf(file, path);
          return readOnly ? (
            <div key={path} className="c-qn-readonly-line">
              <p className="c-qn-line-label">{t(`lines.${path}`)}</p>
              <p className="c-qn-line-text">{value}</p>
            </div>
          ) : (
            <Field key={path} label={t(`lines.${path}`)} {...lineError(path)}>
              {(control) => (
                <Textarea
                  {...control}
                  value={value}
                  onChange={(event) => {
                    change(withLine(survey, language, path, event.currentTarget.value));
                  }}
                />
              )}
            </Field>
          );
        })}
        {readOnly ? (
          <div className="c-qn-readonly-line">
            <p className="c-qn-line-label">{t("lines.ordinals")}</p>
            <p className="c-qn-line-text">{ordinal.ordinal.join(" · ")}</p>
          </div>
        ) : (
          <div className="c-qn-field-with-chip">
            <Field label={t("lines.ordinals")} description={t("lines.ordinals_hint")} {...lineError(ordinal.path)}>
              {(control) => (
                <Textarea
                  {...control}
                  value={ordinal.ordinal.join("\n")}
                  onChange={(event) => {
                    touch(wordingKeyOf(language, null, ordinal.path));
                    change(withOrdinals(survey, language, variant, event.currentTarget.value));
                  }}
                />
              )}
            </Field>
            {added.has(wordingKeyOf(language, null, ordinal.path)) ? (
              <span className="c-qn-chip" role="note">
                <Icon glyph={GLYPH.warningCircle} size="sm" />
                {t("chip.check_wording")}
              </span>
            ) : null}
          </div>
        )}
        {platformLine === null ? null : (
          <div className="c-qn-platform">
            <p className="c-qn-line-label">{t("lines.platform_title")}</p>
            <ul className="c-qn-platform-lines">
              {[platformLine.aiIdentity, platformLine.recordingNotice, platformLine.unknownAnswer].flatMap((line, index) =>
                line === null ? [] : [<li key={String(index)}>{line}</li>],
              )}
            </ul>
          </div>
        )}
      </section>

      {otherProblem.length === 0 ? null : (
        <ul className="c-qn-notes c-qn-file-notes" aria-label={t("bar.other_problems")}>
          {otherProblem.map((one, index) => (
              <li key={String(index)} className="c-qn-note c-qn-note-problem">
                {one.location.language === null || one.location.language === language
                  ? problemText(one.note)
                  : t("card.in_language", { language: languageLabel(one.location.language), text: problemText(one.note) })}
              </li>
            ))}
        </ul>
      )}
      {current === null || current.check.warning.filter((one) => one.code === "how_long_mentions_number").length === 0 ? null : (
        <ul className="c-qn-notes">
          {current.check.warning
            .filter((one) => one.code === "how_long_mentions_number")
            .map((one, index) => (
              <li key={String(index)} className="c-qn-note c-qn-note-warning">
                {warningText(one)}
              </li>
            ))}
        </ul>
      )}

      {readOnly || !savable ? null : (
        <div className="c-qn-bar" role="region" aria-label={t("bar.label")}>
          <span className="c-qn-bar-state">
            <Icon glyph={GLYPH.pencilSimple} size="sm" />
            {firstFormat && !dirty ? t("bar.first_format") : t("bar.unsaved")}
          </span>
          <span className="c-qn-bar-check" role="status">
            {check.isPending || (current === null && refusedProblem === null) ? (
              t("bar.checking")
            ) : problemCount > 0 ? (
              <Button variant="ghost" onClick={jumpToFirstProblem}>
                <Tag status="critical">{t("bar.problem_count", { count: problemCount })}</Tag>
              </Button>
            ) : (
              <Tag status="positive">{t("bar.no_problem")}</Tag>
            )}
            {warningCount === 0 ? null : <Tag status="caution">{t("bar.warning_count", { count: warningCount })}</Tag>}
          </span>
          <Field label={t("bar.change_note")}>
            {(control) => (
              <Input
                {...control}
                maxLength={200}
                value={changeNote}
                onChange={(event) => {
                  setChangeNote(event.currentTarget.value);
                }}
              />
            )}
          </Field>
          <span className="c-qn-bar-actions">
            <Button
              variant="secondary"
              disabled={save.isPending}
              onClick={() => {
                setSurvey(props.survey);
                setChecked(null);
                setRefusedProblem(null);
                setAdded(new Set());
              }}
            >
              {t("bar.discard")}
            </Button>
            <Button
              variant="primary"
              disabled={save.isPending || problemCount > 0}
              onClick={() => {
                save.mutate(survey);
              }}
            >
              <Icon glyph={GLYPH.gitBranch} size="md" />
              <span>{save.isPending ? t("bar.saving") : t("bar.save")}</span>
            </Button>
          </span>
          {save.isError && questionnaireRefusalOf(save.error) === null ? (
            <p className="c-note c-note-critical" role="alert">
              {apiProblemText(save.error, "")}
            </p>
          ) : null}
          {check.isError ? (
            <p className="c-note c-note-critical" role="alert">
              {t("bar.check_failed")} {apiProblemText(check.error, "")}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}

/** A card with the type picker that "Add a question below" opens under it. */
function QuestionCardSlot(
  props: Omit<QuestionCardProps, "position"> & {
    readonly index: number;
    readonly adding: boolean;
    readonly onAdd: (type: QuestionnaireQuestionType) => void;
    readonly onCancelAdd: () => void;
  },
): ReactNode {
  const { index, adding, onAdd, onCancelAdd, ...card } = props;
  return (
    <>
      <QuestionCard {...card} position={index} />
      {adding && index + 1 < props.count ? (
        <li className="c-qn-add-slot">
          <AddQuestion open onOpen={() => undefined} onAdd={onAdd} onCancel={onCancelAdd} />
        </li>
      ) : null}
    </>
  );
}

const QUESTION_TYPE_PICK: readonly QuestionnaireQuestionType[] = ["scale", "choice", "open", "number"];

/** "Add question": a type picker of four buttons. */
function AddQuestion({
  open,
  onOpen,
  onAdd,
  onCancel,
}: {
  readonly open: boolean;
  readonly onOpen: () => void;
  readonly onAdd: (type: QuestionnaireQuestionType) => void;
  readonly onCancel: () => void;
}): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  if (!open) {
    return (
      <div className="c-qn-add">
        <Button variant="secondary" onClick={onOpen}>
          <Icon glyph={GLYPH.plus} size="md" />
          <span>{t("add.question")}</span>
        </Button>
      </div>
    );
  }
  return (
    <div className="c-qn-add c-qn-add-open" role="group" aria-label={t("add.pick_type")}>
      <p className="c-qn-line-label">{t("add.pick_type")}</p>
      <div className="c-qn-add-types">
        {QUESTION_TYPE_PICK.map((type) => (
          <Button
            key={type}
            variant="secondary"
            onClick={() => {
              onAdd(type);
            }}
          >
            {questionTypeLabel(type)}
          </Button>
        ))}
        <Button variant="ghost" onClick={onCancel}>
          {t("action.cancel")}
        </Button>
      </div>
    </div>
  );
}

/** Why the shown questions cannot be edited, and what to do. */
function NotEditableNotice({
  document: doc,
  writeRefusal,
  onShowAgentVersion,
}: {
  readonly document: QuestionnaireDocumentView;
  readonly writeRefusal: string | null;
  readonly onShowAgentVersion: (agentVersionId: string | null) => void;
}): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  if (doc.editable) {
    return writeRefusal === null ? null : (
      <p className="c-note" role="status">
        {writeRefusal}
      </p>
    );
  }
  switch (doc.notEditableReason) {
    case "editor_unavailable":
      return (
        <p className="c-va-alert" role="status">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.info} size="lg" />
          </span>
          <span>
            <strong>{t("readonly.unavailable_title")}</strong> <Tag tone="neutral">{t("readonly.coming_soon")}</Tag>{" "}
            {t("readonly.unavailable_dek")}
          </span>
        </p>
      );
    case "not_the_editable_version":
      return (
        <p className="c-va-alert" role="status">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.info} size="lg" />
          </span>
          <span>
            {t("readonly.other_version", { label: doc.agentVersionLabel ?? "" })}{" "}
            <Button
              variant="ghost"
              onClick={() => {
                onShowAgentVersion(null);
              }}
            >
              {t("readonly.edit_editable")}
            </Button>
          </span>
        </p>
      );
    case "voice_agent_archived":
      return (
        <p className="c-note" role="status">
          {t("readonly.archived")}
        </p>
      );
    default:
      return null;
  }
}

/** The campaigns calling with this VoiceAgent, named before a publish (OD-8). */
export function CampaignDiallingWarning({ campaign }: { readonly campaign: readonly CampaignDiallingView[] }): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  if (campaign.length === 0) return null;
  const named = campaign
    .map((one) => (one.state === "running" ? t("campaign.running", { name: one.name }) : t("campaign.scheduled", { name: one.name })))
    .join(", ");
  return (
    <p className="c-va-alert c-va-alert-flush c-qn-campaign" role="note">
      <span className="c-va-alert-icon">
        <Icon glyph={GLYPH.warning} size="lg" />
      </span>
      <span>
        <strong>{t("campaign.title", { campaigns: named })}</strong> {t("campaign.dek")}
      </span>
    </p>
  );
}

/** The draft, the last save, publish or refusal, and Publish now. */
function OutcomeNotice({
  voiceAgentId,
  document: doc,
  outcome,
  onOutcome,
  baseLabelOf,
  liveVersionSeq,
  survey,
  onOpenAgentVersion,
}: {
  readonly voiceAgentId: string;
  readonly document: QuestionnaireDocumentView;
  readonly outcome: Outcome | null;
  readonly onOutcome: (outcome: Outcome | null) => void;
  readonly baseLabelOf: (agentVersionId: string) => string | null;
  readonly liveVersionSeq: number | null;
  readonly survey: QuestionnaireSurveyJson;
  readonly onOpenAgentVersion: (agentVersionId: string | null) => void;
}): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const publish = useMutation({
    async mutationFn(intent: { readonly agentVersionId: string; readonly label: string; readonly acknowledge: boolean }) {
      if (liveVersionSeq === null) throw new Error(t("publish.not_loaded"));
      await gateway.publishDraftAgentVersion(voiceAgentId, intent.agentVersionId, liveVersionSeq, intent.acknowledge);
      return intent;
    },
    async onSuccess(intent) {
      onOutcome({ kind: "published", label: intent.label });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      await queryClient.invalidateQueries({ queryKey: SURVEY_QUESTIONNAIRE_QUERY_KEY.all });
    },
    async onError(error, intent) {
      const baseChanged = draftBaseChangedOf(error);
      onOutcome(
        baseChanged === null
          ? { kind: "publish_failed", message: apiProblemText(error, "") }
          : { kind: "base_changed", label: intent.label, agentVersionId: intent.agentVersionId, message: baseChanged.message },
      );
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
    },
  });

  const copyEdits = (): void => {
    // Kept to copy (design §41.10, stale): the reload replaces them with the newest questions.
    if ("clipboard" in navigator) void navigator.clipboard.writeText(JSON.stringify(survey, null, 2));
  };

  if (outcome !== null && outcome.kind === "stale") {
    return (
      <div className="c-va-alert c-va-alert-critical" role="alert">
        <span className="c-va-alert-icon">
          <Icon glyph={GLYPH.warningCircle} size="lg" />
        </span>
        <span>
          {t("stale.text")}{" "}
          <Button
            variant="ghost"
            onClick={() => {
              onOutcome(null);
              void queryClient.invalidateQueries({ queryKey: SURVEY_QUESTIONNAIRE_QUERY_KEY.all });
            }}
          >
            {t("stale.reload")}
          </Button>
          <Button variant="ghost" onClick={copyEdits}>
            <Icon glyph={GLYPH.copy} size="sm" />
            <span>{t("stale.copy")}</span>
          </Button>
        </span>
      </div>
    );
  }
  if (outcome !== null && outcome.kind === "unchanged") {
    return (
      <p className="c-va-alert" role="status">
        <span className="c-va-alert-icon">
          <Icon glyph={GLYPH.info} size="lg" />
        </span>
        <span>{t("saved.unchanged", { label: outcome.label })}</span>
      </p>
    );
  }
  if (outcome !== null && outcome.kind === "published") {
    return (
      <p className="c-va-alert" role="status">
        <span className="c-va-alert-icon">
          <Icon glyph={GLYPH.checkCircle} size="lg" />
        </span>
        <span>{t("publish.done", { label: outcome.label })}</span>
      </p>
    );
  }

  // The draft the editor edits now: just saved, or an earlier save not yet published.
  const draftId = outcome !== null && outcome.kind === "saved" ? outcome.draftAgentVersionId : doc.isDraft && doc.editable ? doc.agentVersionId : null;
  if (draftId === null) {
    return outcome !== null && outcome.kind === "publish_failed" ? (
      <p className="c-note c-note-critical" role="alert">
        {outcome.message}
      </p>
    ) : null;
  }
  const draftLabel = outcome !== null && outcome.kind === "saved" ? outcome.draftLabel : (doc.agentVersionLabel ?? "");
  const baseId = outcome !== null && outcome.kind === "saved" ? outcome.baseAgentVersionId : doc.baseAgentVersionId;
  const baseLabel = baseId === null ? null : baseLabelOf(baseId);
  return (
    <div className="c-va-card c-qn-draft" role="status">
      <p className="c-va-card-title">
        {outcome !== null && outcome.kind === "saved"
          ? baseLabel === null
            ? t("saved.title_first", { label: draftLabel })
            : t("saved.title", { label: draftLabel, base: baseLabel })
          : baseLabel === null
            ? t("draft.title_first", { label: draftLabel })
            : t("draft.title", { label: draftLabel, base: baseLabel })}
      </p>
      <p className="c-va-card-dek">{t("draft.dek")}</p>
      <CampaignDiallingWarning campaign={doc.campaignDialling} />
      {outcome !== null && outcome.kind === "base_changed" ? (
        <p className="c-note c-note-critical" role="alert">
          {outcome.message}
        </p>
      ) : null}
      {outcome !== null && outcome.kind === "publish_failed" ? (
        <p className="c-note c-note-critical" role="alert">
          {outcome.message}
        </p>
      ) : null}
      <div className="c-va-actions">
        <Button
          variant="primary"
          disabled={publish.isPending || liveVersionSeq === null}
          onClick={() => {
            publish.mutate({ agentVersionId: draftId, label: draftLabel, acknowledge: outcome !== null && outcome.kind === "base_changed" });
          }}
        >
          <Icon glyph={GLYPH.rocketLaunch} size="md" />
          <span>
            {publish.isPending
              ? t("publish.working")
              : outcome !== null && outcome.kind === "base_changed"
                ? t("publish.anyway", { label: draftLabel })
                : t("publish.now", { label: draftLabel })}
          </span>
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            onOpenAgentVersion(draftId);
          }}
        >
          {t("action.view_in_versions")}
        </Button>
      </div>
    </div>
  );
}

/** The file's name, as the api names it: `<agent>-v<n>-questions.<json|md>`. */
export function questionnaireDownloadNameOf(displayName: string, agentVersionLabel: string, fileFormat: QuestionnaireFileFormatView): string {
  const ordinal = Number(agentVersionLabel.replace(/^v/u, ""));
  try {
    return questionnaireFileNameOf(displayName, ordinal, fileFormat);
  } catch {
    return `questions-${agentVersionLabel === "" ? "draft" : agentVersionLabel}.${fileFormat}`;
  }
}

/** Download questions: JSON (uploaded back through Edit it is used as it is) or Markdown, one language's file. */
export function DownloadActions({
  voiceAgentId,
  agentVersionId,
  agentVersionLabel,
  languages,
  language,
  displayName,
}: {
  readonly voiceAgentId: string;
  readonly agentVersionId: string;
  readonly agentVersionLabel: string;
  readonly languages: readonly string[];
  readonly language: string;
  readonly displayName: string;
}): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  const gateway = useGateway();
  const download = useMutation({
    async mutationFn(fileFormat: QuestionnaireFileFormatView) {
      const file = await gateway.downloadQuestionnaireFile(voiceAgentId, agentVersionId, fileFormat, languages.length > 1 ? language : null);
      saveQuestionnaireFile(file, questionnaireDownloadNameOf(displayName, agentVersionLabel, fileFormat));
    },
  });
  return (
    <div className="c-va-actions c-qn-download">
      <Button
        variant="secondary"
        disabled={download.isPending}
        onClick={() => {
          download.mutate("json");
        }}
      >
        <Icon glyph={GLYPH.downloadSimple} size="md" />
        <span>{languages.length > 1 ? t("download.json_language", { language: languageLabel(language) }) : t("download.json")}</span>
      </Button>
      <Button
        variant="ghost"
        disabled={download.isPending}
        onClick={() => {
          download.mutate("md");
        }}
      >
        <span>{languages.length > 1 ? t("download.markdown_language", { language: languageLabel(language) }) : t("download.markdown")}</span>
      </Button>
      {download.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {apiProblemText(download.error, "")}
        </p>
      ) : null}
    </div>
  );
}
